// Pure helpers behind the แฟลชเซล (flash sale campaign) page —
// src/views/AppCatalog/FlashSaleEdit.vue and the list in Promotions.vue.
// Spec: docs/specs/2026-10-05-flash-sale-campaign-page-design.md §6.4.
//
// Vue-free / axios-free on purpose: this repo's jest cannot mount a .vue file
// (see src/utils/wmsImportModal.js header), so every rule worth testing lives
// here and the page only renders it. Behaviour: tests/unit/flashCampaign.spec.js.
//
// TIME. The picker emits a wall-clock string ("yyyy-MM-dd HH:mm:ss") and the
// old dialog turned it into an instant with `new Date(str)` — i.e. in the
// MACHINE's timezone. A staff laptop set to anything but Asia/Bangkok then
// saved a different moment from the one typed. Thailand has been at a fixed
// +07:00 with no DST since 1920, so the conversion here is plain arithmetic on
// a constant offset and never asks the machine (or Intl) what zone it is in.

import { skuRowsOf } from "./sellerProductRow";

export const BANGKOK_OFFSET_MS = 7 * 60 * 60 * 1000;

/** Page sizes for both the products table and the add-product modal (§3, §4). */
export const FLASH_PAGE_SIZES = [10, 20, 50];
export const FLASH_DEFAULT_PAGE_SIZE = 20;

/** BE-6 accepts 1..200 product ids per POST. */
export const FLASH_MAX_SELECTION = 200;

const pad = (n) => (n < 10 ? "0" + n : String(n));

function str(v) {
  return v === null || v === undefined ? "" : String(v).trim();
}

const PICKER_RE = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * bangkokToRFC3339("2026-10-05 12:00:00") → "2026-10-05T12:00:00+07:00".
 * The wall clock is taken AS Bangkok time, whatever the machine's zone.
 * Anything that is not a real calendar moment (bad format, 2026-02-30, 25:00) → null.
 */
export function bangkokToRFC3339(value) {
  const m = PICKER_RE.exec(str(value));
  if (!m) return null;
  const y = +m[1], mo = +m[2], d = +m[3], h = +m[4], mi = +m[5], s = m[6] === undefined ? 0 : +m[6];
  if (mo < 1 || mo > 12 || h > 23 || mi > 59 || s > 59) return null;
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) return null;
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${pad(s)}+07:00`;
}

/** Epoch ms of an RFC3339 / ISO instant, or null. Go/MariaDB zero dates are null. */
export function instantMs(value) {
  if (value instanceof Date) {
    const t = value.getTime();
    return isFinite(t) ? t : null;
  }
  if (typeof value === "number") return isFinite(value) ? value : null;
  const s = str(value);
  if (s === "" || s.indexOf("0001-01-01") === 0 || s.indexOf("0000-00-00") === 0) return null;
  // A bare picker string has no zone; read it as Bangkok rather than letting
  // Date.parse guess the machine's.
  if (PICKER_RE.test(s) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
    const iso = bangkokToRFC3339(s);
    return iso ? Date.parse(iso) : null;
  }
  const t = Date.parse(s);
  return isFinite(t) ? t : null;
}

function bangkokParts(ms) {
  const d = new Date(ms + BANGKOK_OFFSET_MS);
  return {
    y: String(d.getUTCFullYear()),
    mo: pad(d.getUTCMonth() + 1),
    d: pad(d.getUTCDate()),
    h: pad(d.getUTCHours()),
    mi: pad(d.getUTCMinutes()),
    s: pad(d.getUTCSeconds())
  };
}

/**
 * rfc3339ToBangkokPicker("2026-10-05T05:00:00Z") → "2026-10-05 12:00:00" —
 * the picker's format, in Bangkok wall-clock, independent of the machine. null when unusable.
 */
export function rfc3339ToBangkokPicker(value) {
  const ms = instantMs(value);
  if (ms === null) return null;
  const p = bangkokParts(ms);
  return `${p.y}-${p.mo}-${p.d} ${p.h}:${p.mi}:${p.s}`;
}

/** formatBangkok(iso) → "DD/MM/YYYY HH:mm" in Bangkok time, or "-". */
export function formatBangkok(value) {
  const ms = instantMs(value);
  if (ms === null) return "-";
  const p = bangkokParts(ms);
  return `${p.d}/${p.mo}/${p.y} ${p.h}:${p.mi}`;
}

/**
 * campaignPhase({status, starts_at, ends_at}, now) →
 *   'draft' | 'ended'   straight from status;
 *   active → 'upcoming' (now < start) · 'ongoing' (start ≤ now ≤ end, both
 *   bounds INCLUSIVE like Member_price.go loadCampaigns) · 'expired' (now > end).
 * A missing bound is open on that side.
 */
export function campaignPhase(campaign, now) {
  const c = campaign && typeof campaign === "object" ? campaign : {};
  const status = str(c.status);
  if (status === "draft") return "draft";
  if (status === "ended") return "ended";
  const n = now === undefined || now === null ? Date.now() : instantMs(now);
  const start = instantMs(c.starts_at);
  const end = instantMs(c.ends_at);
  if (start !== null && n < start) return "upcoming";
  if (end !== null && n > end) return "expired";
  return "ongoing";
}

const PHASE_TAG = {
  draft: "info",
  upcoming: "warning",
  ongoing: "success",
  expired: "danger",
  ended: "danger"
};

/** el-tag type for a phase. */
export function phaseTagType(phase) {
  return PHASE_TAG[phase] || "info";
}

function numberOrNull(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v !== "number" && typeof v !== "string") return null;
  const n = Number(v);
  return isFinite(n) ? n : null;
}

/**
 * validateHeader(form, total) → [] or a list of full i18n keys
 * ('errors.campaign.name_required' | 'errors.campaign.invalid_date_range' |
 *  'errors.campaign.invalid_value' | 'errors.campaign.no_targets').
 *
 * form = { name, kind, value, dateRange: [startPicker, endPicker], status };
 * total = how many products the campaign holds (0 for a new one) — activating
 * an empty campaign is refused here before BE refuses it (E3).
 */
export function validateHeader(form, total) {
  const f = form && typeof form === "object" ? form : {};
  const out = [];
  if (str(f.name) === "") out.push("errors.campaign.name_required");
  const range = Array.isArray(f.dateRange) ? f.dateRange : [];
  const s = instantMs(bangkokToRFC3339(range[0]));
  const e = instantMs(bangkokToRFC3339(range[1]));
  if (s === null || e === null || e <= s) out.push("errors.campaign.invalid_date_range");
  const v = numberOrNull(f.value);
  const kind = str(f.kind);
  if (v === null || v <= 0 || (kind === "percent_off" && v > 100) || (kind !== "percent_off" && kind !== "fixed_off")) {
    out.push("errors.campaign.invalid_value");
  }
  if (str(f.status) === "active" && !(Number(total) > 0)) out.push("errors.campaign.no_targets");
  return out;
}

/**
 * headerPayload(form) → the PATCH/POST body. NEVER carries `targets`: PUT
 * /campaigns/:id replaces every target with what it is sent, and this page
 * only ever holds one page of products (spec §0.1).
 */
export function headerPayload(form) {
  const f = form && typeof form === "object" ? form : {};
  const range = Array.isArray(f.dateRange) ? f.dateRange : [];
  return {
    name: str(f.name),
    kind: str(f.kind),
    value: Number(f.value),
    starts_at: bangkokToRFC3339(range[0]),
    ends_at: bangkokToRFC3339(range[1]),
    status: str(f.status)
  };
}

/** The form's starting state built from a campaign the API returned. */
export function formFromCampaign(campaign) {
  const c = campaign && typeof campaign === "object" ? campaign : {};
  const s = rfc3339ToBangkokPicker(c.starts_at);
  const e = rfc3339ToBangkokPicker(c.ends_at);
  return {
    name: str(c.name),
    kind: str(c.kind) || "percent_off",
    value: numberOrNull(c.value) === null ? 0 : Number(c.value),
    dateRange: s && e ? [s, e] : null,
    status: str(c.status) || "draft"
  };
}

function positiveInt(v) {
  const raw = v && typeof v === "object" ? (v.id !== undefined ? v.id : v.product_id) : v;
  const n = Number(raw);
  return isFinite(n) && n > 0 && Math.floor(n) === n ? n : null;
}

/**
 * addProductsPayload(selection) → { product_ids } — unique positive integers,
 * ascending. `selection` is what <el-table reserve-selection> reports: the
 * rows ticked on EVERY page, or plain ids.
 */
export function addProductsPayload(selection) {
  const list = Array.isArray(selection) ? selection : [];
  const seen = {};
  const ids = [];
  for (let i = 0; i < list.length; i++) {
    const id = positiveInt(list[i]);
    if (id === null || seen[id]) continue;
    seen[id] = true;
    ids.push(id);
  }
  ids.sort((a, b) => a - b);
  return { product_ids: ids };
}

/** How many distinct products the cross-page selection holds. */
export function selectionCount(selection) {
  return addProductsPayload(selection).product_ids.length;
}

/** The ยืนยัน button: 1..200 distinct products. */
export function canConfirmSelection(selection) {
  const n = selectionCount(selection);
  return n > 0 && n <= FLASH_MAX_SELECTION;
}

/** el-table `:selectable` for the picker: a product already in the campaign cannot be ticked. */
export function pickerRowSelectable(row) {
  if (!row) return false;
  const v = row.in_campaign;
  if (v === true || v === 1 || v === "1" || v === "true") return false;
  return true;
}

function pageSizeOf(v) {
  const n = Number(v);
  return FLASH_PAGE_SIZES.indexOf(n) >= 0 ? n : FLASH_DEFAULT_PAGE_SIZE;
}

function pageOf(v) {
  const n = Number(v);
  return isFinite(n) && n >= 1 ? Math.floor(n) : 1;
}

/** BE-3 query: GET /campaigns/:id/products?page=&page_size=&keyword= */
export function pageParams(opts) {
  const o = opts || {};
  return { page: pageOf(o.page), page_size: pageSizeOf(o.pageSize), keyword: str(o.keyword) };
}

/** BE-5 query for the picker: GET /products?keyword=&page=&limit=&campaign_id= */
export function pickerParams(opts) {
  const o = opts || {};
  const out = { keyword: str(o.keyword), page: pageOf(o.page), limit: pageSizeOf(o.pageSize) };
  const cid = positiveInt(o.campaignId);
  if (cid !== null) out.campaign_id = cid;
  return out;
}

/** name_th || name_en || name || code */
export function productDisplayName(row) {
  const r = row && typeof row === "object" ? row : {};
  return str(r.name_th) || str(r.name_en) || str(r.name) || str(r.code);
}

/**
 * money(n) → "1,290.00". Grouped by hand so output does not depend on ICU.
 */
export function money(v) {
  const n = numberOrNull(v);
  if (n === null) return "-";
  const parts = Math.abs(n).toFixed(2).split(".");
  return (n < 0 ? "-" : "") + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "." + parts[1];
}

/** priceRangeText(min, max) → "590.00~890.00", one number when equal, "-" when absent. */
export function priceRangeText(min, max) {
  const a = numberOrNull(min);
  const b = numberOrNull(max);
  if (a === null && b === null) return "-";
  if (a === null || b === null || Math.abs(a - b) < 0.005) return money(a === null ? b : a);
  return money(Math.min(a, b)) + "~" + money(Math.max(a, b));
}

/**
 * skuFlashNote(sku) → null, or { key, price } when a per-SKU flash price
 * (Shopee-owned or set by hand) overlaps the campaign window and therefore
 * wins over the campaign for that SKU (E1).
 */
export function skuFlashNote(sku) {
  const s = sku && typeof sku === "object" ? sku : {};
  const v = s.flash_overlaps;
  if (!(v === true || v === 1 || v === "1" || v === "true")) return null;
  return {
    key: str(s.flash_source) === "shopee" ? "app_catalog.flash_sale_flash_shopee" : "app_catalog.flash_sale_flash_manual",
    price: money(s.flash_price)
  };
}

/**
 * campaignSkuRows(res) → BE-4's skus as skuRowsOf rows (image, sku_name,
 * sku_code, price, quantity, is_active, flash_*) plus the two fields only this
 * endpoint has: campaign_price (computed by BE — never in FE) and flash_overlaps.
 */
export function campaignSkuRows(res) {
  const body = res && res.data && !Array.isArray(res.data) ? res.data : res || {};
  const detail = body.data && typeof body.data === "object" && !Array.isArray(body.data) ? body.data : body;
  const raw = Array.isArray(detail.skus) ? detail.skus : [];
  return skuRowsOf(detail).map((row, i) => {
    const s = raw[i] && typeof raw[i] === "object" ? raw[i] : {};
    return Object.assign({}, row, {
      campaign_price: numberOrNull(s.campaign_price),
      flash_overlaps: s.flash_overlaps === true || s.flash_overlaps === 1 || s.flash_overlaps === "1" || s.flash_overlaps === "true"
    });
  });
}

/** BE-3 body (axios response or body) → { rows, total, page, pageSize, hiddenDeleted, campaign }. */
export function campaignProductsBody(res) {
  const body = res && res.data && !Array.isArray(res.data) ? res.data : res || {};
  const rows = Array.isArray(body.data) ? body.data : [];
  const total = numberOrNull(body.total);
  return {
    rows,
    total: total === null || total < 0 ? rows.length : Math.floor(total),
    page: pageOf(body.page),
    pageSize: pageSizeOf(body.page_size),
    hiddenDeleted: numberOrNull(body.hidden_deleted) || 0,
    campaign: body.campaign && typeof body.campaign === "object" ? body.campaign : null
  };
}

export default {
  BANGKOK_OFFSET_MS,
  FLASH_PAGE_SIZES,
  FLASH_DEFAULT_PAGE_SIZE,
  FLASH_MAX_SELECTION,
  bangkokToRFC3339,
  instantMs,
  rfc3339ToBangkokPicker,
  formatBangkok,
  campaignPhase,
  phaseTagType,
  validateHeader,
  headerPayload,
  formFromCampaign,
  addProductsPayload,
  selectionCount,
  canConfirmSelection,
  pickerRowSelectable,
  pageParams,
  pickerParams,
  productDisplayName,
  money,
  priceRangeText,
  skuFlashNote,
  campaignSkuRows,
  campaignProductsBody
};
